import React, { useState, useEffect, useCallback } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faXmark,
  faCircleCheck,
  faClock,
  faCircleXmark,
  faArrowRotateLeft,
  faEye,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

const STATUS_CONFIG = {
  APPROVED:  { label: "Approved",  color: "bg-green-100 text-green-700",  icon: faCircleCheck },
  INITIATED: { label: "Pending",   color: "bg-yellow-100 text-yellow-700", icon: faClock },
  REJECTED:  { label: "Rejected",  color: "bg-red-100 text-red-700",      icon: faCircleXmark },
  WITHDRAWN: { label: "Withdrawn", color: "bg-gray-100 text-gray-600",    icon: faArrowRotateLeft },
};

const StatCard = ({ label, value, sub, accent }) => (
  <div className={`bg-white rounded-xl border p-5 ${accent ? "border-l-4 " + accent : "border-gray-100"}`}>
    <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-800">{value ?? "—"}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "bg-gray-100 text-gray-600", icon: faClock };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
      <FontAwesomeIcon icon={cfg.icon} className="text-[10px]" />
      {cfg.label}
    </span>
  );
};

const DetailModal = ({ closure, onClose }) => {
  if (!closure) return null;

  const seekerA = closure.connected_matchseeker_id;
  const bgA = closure.bg_profile_id;
  const seekerB = closure.matched_with_connected_matchseeker_id;
  const bgB = closure.matched_with_connected_bg_profile_id;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-pink-50 flex items-center justify-center">
              <FontAwesomeIcon icon={faHeart} className="text-pink-500 text-sm" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-sm">Closure Request</h2>
              <p className="text-xs text-gray-400">{closure._id}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-center gap-3">
            <StatusBadge status={closure.closure_status} />
            <span className="text-xs text-gray-400">
              Updated {new Date(closure.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
            </span>
          </div>

          {/* Side A */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-3">Party A</p>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-gray-800">{seekerA?.fullName || "—"}</p>
                <p className="text-xs text-gray-500">{seekerA?.emailId || "—"}</p>
                {bgA && (
                  <p className="text-xs text-gray-500 mt-2">
                    Profile: <span className="font-medium text-gray-700">{bgA.firstName || "—"}</span>
                    {bgA.gender && <span className="ml-1 text-gray-400">({bgA.gender})</span>}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-pink-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-pink-600 uppercase tracking-wider mb-3">Party B</p>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-gray-800">{seekerB?.fullName || "—"}</p>
                <p className="text-xs text-gray-500">{seekerB?.emailId || "—"}</p>
                {bgB && (
                  <p className="text-xs text-gray-500 mt-2">
                    Profile: <span className="font-medium text-gray-700">{bgB.firstName || "—"}</span>
                    {bgB.gender && <span className="ml-1 text-gray-400">({bgB.gender})</span>}
                  </p>
                )}
              </div>
            </div>
          </div>

          {closure.feedback_details && (
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Feedback</p>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                {JSON.stringify(closure.feedback_details, null, 2)}
              </pre>
            </div>
          )}

          <div className="flex gap-2 text-xs text-gray-400">
            <span>Created: {new Date(closure.createdAt).toLocaleString("en-IN")}</span>
            <span>·</span>
            <span>Updated: {new Date(closure.updatedAt).toLocaleString("en-IN")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const MatchClosures = () => {
  const [stats, setStats]           = useState(null);
  const [closures, setClosures]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [selected, setSelected]     = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const limit = 20;

  const fetchStats = useCallback(async () => {
    try {
      const res = await func.get(urls.CLOSURE_STATS_URL);
      if (res?.status === 200) setStats(res.data.stats);
    } catch { /* silent */ }
  }, []);

  const fetchClosures = useCallback(async (page, status) => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (status) params.status = status;
      const res = await func.get(urls.LIST_CLOSURES_URL, { params });
      if (res?.status === 200) {
        setClosures(res.data.closures || []);
        setTotalPages(res.data.totalPages || 1);
        setTotal(res.data.total || 0);
      }
    } catch {
      toast.error("Failed to load closures");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchStats();
    fetchClosures(1, "");
  }, [fetchStats, fetchClosures]);

  const applyFilter = () => {
    setCurrentPage(1);
    fetchClosures(1, statusFilter);
    setShowFilter(false);
  };

  const clearFilter = () => {
    setStatusFilter("");
    setCurrentPage(1);
    fetchClosures(1, "");
    setShowFilter(false);
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await func.get(urls.GET_CLOSURE_DETAIL_URL + id);
      if (res?.status === 200) setSelected(res.data.closure);
    } catch {
      toast.error("Failed to load detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
    fetchClosures(p, statusFilter);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FontAwesomeIcon icon={faTrophy} className="text-[#C9A84C]" />
            Match Closures
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">Successful matches — the platform's core KPI</p>
        </div>
        <button
          onClick={() => setShowFilter(v => !v)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition"
        >
          <FontAwesomeIcon icon={faFilter} className="text-xs" />
          Filter
          {statusFilter && <span className="w-2 h-2 rounded-full bg-[#C9A84C]" />}
        </button>
      </div>

      {/* Filter panel */}
      {showFilter && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 flex flex-wrap gap-4 items-end shadow-sm">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30"
            >
              <option value="">All statuses</option>
              <option value="APPROVED">Approved (Successful)</option>
              <option value="INITIATED">Pending</option>
              <option value="REJECTED">Rejected</option>
              <option value="WITHDRAWN">Withdrawn</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={applyFilter} className="px-4 py-2 bg-[#C9A84C] text-white rounded-lg text-sm font-medium hover:bg-[#b8953f] transition">Apply</button>
            <button onClick={clearFilter} className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition">Clear</button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="Total Requests" value={stats?.total} accent="border-gray-300" />
        <StatCard label="Successful Matches" value={stats?.approved} sub={`${stats?.approvedThisMonth ?? 0} this month`} accent="border-green-400" />
        <StatCard label="Pending" value={stats?.initiated} accent="border-yellow-400" />
        <StatCard label="Rejected" value={stats?.rejected} accent="border-red-400" />
        <StatCard label="Withdrawn" value={stats?.withdrawn} accent="border-gray-300" />
        <StatCard
          label="Success Rate"
          value={stats?.total ? `${Math.round((stats.approved / stats.total) * 100)}%` : "—"}
          sub="approved / total"
          accent="border-[#C9A84C]"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-700">{total} closure requests</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : closures.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No closure requests found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Party A</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Profile A</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Party B</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Profile B</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {closures.map((c) => {
                  const seekerA = c.connected_matchseeker_id;
                  const bgA     = c.bg_profile_id;
                  const seekerB = c.matched_with_connected_matchseeker_id;
                  const bgB     = c.matched_with_connected_bg_profile_id;
                  return (
                    <tr key={c._id} className="hover:bg-gray-50 transition">
                      <td className="px-5 py-3.5">
                        <p className="font-medium text-gray-800">{seekerA?.fullName || "—"}</p>
                        <p className="text-xs text-gray-400">{seekerA?.emailId || ""}</p>
                      </td>
                      <td className="px-5 py-3.5 text-gray-600">{bgA?.firstName || "—"}</td>
                      <td className="px-5 py-3.5">
                        <p className="font-medium text-gray-800">{seekerB?.fullName || "—"}</p>
                        <p className="text-xs text-gray-400">{seekerB?.emailId || ""}</p>
                      </td>
                      <td className="px-5 py-3.5 text-gray-600">{bgB?.firstName || "—"}</td>
                      <td className="px-5 py-3.5">
                        <StatusBadge status={c.closure_status} />
                      </td>
                      <td className="px-5 py-3.5 text-xs text-gray-400 whitespace-nowrap">
                        {new Date(c.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                      <td className="px-5 py-3.5">
                        <button
                          onClick={() => openDetail(c._id)}
                          disabled={detailLoading}
                          className="text-gray-400 hover:text-[#C9A84C] transition p-1"
                          title="View detail"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-4 border-t border-gray-50 flex items-center justify-between">
            <p className="text-xs text-gray-400">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-8 h-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
              </button>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-8 h-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && <DetailModal closure={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default MatchClosures;
